import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw, Edit, Play, BookOpen, Users } from 'lucide-react';
import { toast } from 'sonner';
import { getEnrichmentQueue, updateBookISBN, enrichSingleBook, QueuedBook } from '@/utils/enrichmentQueue';
import { CoverImage } from '@/components/CoverImage';

export function EnrichmentQueueTable() {
  const [books, setBooks] = useState<QueuedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    book: QueuedBook | null;
    isbn13: string;
    isbn10: string;
  }>({
    open: false,
    book: null,
    isbn13: '',
    isbn10: ''
  });
  const [enriching, setEnriching] = useState<string | null>(null);

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    try {
      setLoading(true);
      const data = await getEnrichmentQueue(100);
      setBooks(data);
    } catch (error) {
      console.error('Failed to load queue:', error);
      toast.error('Failed to load enrichment queue');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (book: QueuedBook) => {
    setEditDialog({
      open: true,
      book,
      isbn13: book.isbn13 || '',
      isbn10: book.isbn10 || ''
    });
  };

  const closeEditDialog = () => {
    setEditDialog({
      open: false,
      book: null,
      isbn13: '',
      isbn10: ''
    });
  };

  const handleSaveISBN = async () => {
    if (!editDialog.book) return;

    try {
      await updateBookISBN(
        editDialog.book.book_id,
        editDialog.isbn13 || null,
        editDialog.isbn10 || null
      );

      toast.success('ISBN updated! Book queued for re-enrichment.');
      closeEditDialog();
      await loadQueue();
    } catch (error) {
      console.error('Failed to update ISBN:', error);
      toast.error('Failed to update ISBN');
    }
  };

  const handleEnrichNow = async (book: QueuedBook) => {
    try {
      setEnriching(book.book_id);
      const result = await enrichSingleBook(book.book_id);
      
      if (result.enriched > 0) {
        toast.success(`Successfully enriched "${book.title}"`);
      } else {
        toast.warning(`No new data found for "${book.title}"`);
      }
      
      await loadQueue();
    } catch (error) {
      console.error('Failed to enrich book:', error);
      toast.error('Failed to enrich book');
    } finally {
      setEnriching(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      pending: { variant: 'secondary', label: 'Pending' },
      processing: { variant: 'default', label: 'Processing' },
      enriched: { variant: 'default', label: 'Enriched' },
      failed: { variant: 'destructive', label: 'Failed' },
      no_data_found: { variant: 'outline', label: 'No Data' }
    };

    const config = variants[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant as any}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enrichment Queue Inspector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Enrichment Queue Inspector
              </CardTitle>
              <CardDescription>
                Books needing enrichment - prioritized by user library presence
              </CardDescription>
            </div>
            <Button onClick={loadQueue} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {books.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No books in enrichment queue
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Cover</TableHead>
                    <TableHead>Title / Authors</TableHead>
                    <TableHead>ISBN</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {books.map((book) => (
                    <TableRow key={book.book_id}>
                      <TableCell>
                        <CoverImage
                          coverUrl={book.cover_url || undefined}
                          title={book.title}
                          author={book.authors?.[0]}
                          isbn13={book.isbn13 || undefined}
                          isbn10={book.isbn10 || undefined}
                          size="sm"
                          className="w-12 h-16 object-cover rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium line-clamp-2">{book.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {book.authors?.join(', ') || 'Unknown'}
                          </div>
                          {book.in_user_library && (
                            <Badge variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />
                              In {book.user_count || 0} libraries
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          {book.isbn13 ? (
                            <div className="font-mono">{book.isbn13}</div>
                          ) : book.isbn10 ? (
                            <div className="font-mono">{book.isbn10}</div>
                          ) : (
                            <Badge variant="outline" className="text-xs">No ISBN</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(book.enrichment_status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {book.synopsis && (
                            <Badge variant="outline" className="text-xs">Synopsis</Badge>
                          )}
                          {book.page_count && (
                            <Badge variant="outline" className="text-xs">Pages</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(book)}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit ISBN
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleEnrichNow(book)}
                            disabled={enriching === book.book_id}
                          >
                            {enriching === book.book_id ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3 mr-1" />
                            )}
                            Enrich Now
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialog.open} onOpenChange={closeEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit ISBN for "{editDialog.book?.title}"</DialogTitle>
            <DialogDescription>
              Update the ISBN to trigger re-enrichment with better data sources
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="isbn13">ISBN-13</Label>
              <Input
                id="isbn13"
                placeholder="Enter 13-digit ISBN"
                value={editDialog.isbn13}
                onChange={(e) => setEditDialog({ ...editDialog, isbn13: e.target.value })}
                maxLength={13}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="isbn10">ISBN-10</Label>
              <Input
                id="isbn10"
                placeholder="Enter 10-digit ISBN"
                value={editDialog.isbn10}
                onChange={(e) => setEditDialog({ ...editDialog, isbn10: e.target.value })}
                maxLength={10}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              Cancel
            </Button>
            <Button onClick={handleSaveISBN}>
              Save & Queue for Re-enrichment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
